// frontend/src/services/adminPosts.js
//
// Admin Posts tab + manual lead creation API client.
//
// Covers the admin-only monitoring projection over `posts`
// (`GET /admin/posts`, `GET /admin/posts/{id}`) and the manual
// lead-creation surface (`POST /admin/leads/manual` and its
// `generate-reasoning` companion). Kept separate from the generic
// `posts.js` service (which hits the CRUD `/posts` routes) because
// these are a distinct admin concern with their own payload shapes.

import api from "./api";

// ---------------------------------------------------------------------------
// Post-detail cache
// ---------------------------------------------------------------------------
//
// The detail window opens from two admin surfaces (the Posts tab and the
// dashboard's lead cards), often for the same post several times in a row.
// A small in-memory cache keeps re-opens instant:
//
//   - STALE-WHILE-REVALIDATE (the window's policy): on EVERY open the window
//     shows the cached detail at once (peekPostDetail - synchronous, so a
//     cached open paints no skeleton frame) AND revalidates it in the
//     background with one request (getPostDetail(id, {revalidate: true})),
//     replacing what it shows when the fresh response lands. A post's lead
//     status/owner changes on the dashboard, and its group can be marked
//     private (the server then redacts the content), so a cached copy is only
//     ever a first paint, never the final word. A failed revalidation keeps
//     the cached copy on screen with no error UI.
//   - FRESHNESS: an entry is servable for DETAIL_TTL_MS; an expired one is
//     never shown (peek ignores it, a plain read drops it and fetches again).
//   - SIZE: at most DETAIL_CACHE_MAX entries; inserting past the cap evicts the
//     oldest (a Map iterates in insertion order, and a re-cached id is moved to
//     the end).
//   - IN-FLIGHT DEDUPE: concurrent calls for the same id (revalidations
//     included) share one request. A rejected request is removed and never
//     cached, so the next call retries.
//   - INVALIDATION: invalidatePostDetail(id) drops the entry AND detaches any
//     in-flight request, so a response that left the server before the
//     invalidation cannot repopulate the cache with pre-change data.
//     clearPostDetailCache() does the same for every id (on logout).
//
// Admin-only data, in memory only (never localStorage): it is gone on reload.
export const DETAIL_TTL_MS = 5 * 60 * 1000;
export const DETAIL_CACHE_MAX = 50;

/** postId -> { data, at } */
const detailCache = new Map();
/** postId -> Promise of the in-flight request */
const detailInFlight = new Map();

/** The entry's data when it is still within the TTL, else undefined. Pure. */
function freshData(entry, now) {
  if (!entry || now - entry.at >= DETAIL_TTL_MS) return undefined;
  return entry.data;
}

function readFreshDetail(postId, now) {
  const data = freshData(detailCache.get(postId), now);
  if (data === undefined) detailCache.delete(postId);
  return data;
}

function storeDetail(postId, data) {
  detailCache.delete(postId);
  detailCache.set(postId, { data, at: Date.now() });
  while (detailCache.size > DETAIL_CACHE_MAX) {
    detailCache.delete(detailCache.keys().next().value);
  }
}

/**
 * The cached detail for one post, synchronously, without fetching and without
 * touching the cache - safe to call during render.
 * @param {string} postId
 * @returns {Object|undefined} the cached payload, or undefined when there is
 *   none or it is older than DETAIL_TTL_MS.
 */
export function peekPostDetail(postId) {
  return freshData(detailCache.get(postId), Date.now());
}

/**
 * Full detail for one post, cached (see the block comment above).
 * @param {string} postId
 * @param {{force?: boolean, revalidate?: boolean}} [options]
 *   - revalidate: skip the cached entry and ask the server, but JOIN a request
 *     already in flight for this id (the window's background revalidation).
 *   - force: skip the cache AND any in-flight request and fetch anew (the new
 *     request replaces the old one) - the window's explicit retry.
 * @returns {Promise<Object>}
 */
export function getPostDetail(postId, { force = false, revalidate = false } = {}) {
  if (!force) {
    if (!revalidate) {
      const cached = readFreshDetail(postId, Date.now());
      if (cached !== undefined) return Promise.resolve(cached);
    }
    const pending = detailInFlight.get(postId);
    if (pending) return pending;
  }

  const request = api.get(`/admin/posts/${postId}`).then(
    (response) => {
      // Only the request still registered for this id may write: one that was
      // invalidated or superseded (force) while in flight is answered to its
      // own callers but never cached.
      if (detailInFlight.get(postId) === request) {
        detailInFlight.delete(postId);
        storeDetail(postId, response.data);
      }
      return response.data;
    },
    (error) => {
      if (detailInFlight.get(postId) === request) detailInFlight.delete(postId);
      throw error;
    }
  );
  detailInFlight.set(postId, request);
  return request;
}

/**
 * Forget one post's cached detail (e.g. after a manual lead was created from
 * it, so its leads list refreshes on the next open).
 * @param {string} postId
 */
export function invalidatePostDetail(postId) {
  detailCache.delete(postId);
  detailInFlight.delete(postId);
}

/**
 * Forget every cached detail and detach every in-flight request (their
 * responses still answer their own callers but are never cached). Called on
 * logout, so the next account on this tab never sees the previous admin's
 * cached posts. Synchronous and network-free.
 */
export function clearPostDetailCache() {
  detailCache.clear();
  detailInFlight.clear();
}

const adminPostsService = {
  /**
   * Paginated, newest-first posts list for the admin Posts tab.
   * @param {Object} params - { skip, limit, statuses: string[], search, created_after, created_before }
   * @returns {Promise<{items, total, skip, limit}>}
   */
  listPosts: async (params = {}) => {
    const response = await api.get("/admin/posts", { params });
    return response.data;
  },

  /**
   * Full detail for one post (content, analysis, created leads, ingestion
   * attribution, Keyword Gate verdict). Cached - see getPostDetail above.
   */
  getPostDetail,

  peekPostDetail,

  invalidatePostDetail,

  clearPostDetailCache,

  /**
   * Create a manual lead (or, with dry_run, preview the matched users).
   * @param {Object} payload - see ManualLeadCreateRequest on the backend.
   *   Optional `override_anonymous_preference: true` alerts recipients who
   *   muted anonymous-author alerts anyway; send it ONLY on an explicit admin
   *   opt-in, and never with notify=false (the server answers 422).
   *   Optional `group_link` (paste mode only, trimmed, omitted when empty) lets
   *   the server resolve whether the pasted post is from a private group, on
   *   the dry-run and the create alike; the created post stores it.
   * @returns {Promise<Object>} dry-run -> {matched_users, user_ids, users,
   *   dry_run, post_author_is_anonymous}
   *   where users[] = {user_id, full_name, email, user_type, anonymous_alerts}
   *   (the recipient picker's rows). `post_author_is_anonymous` may be absent
   *   on an older server - treat absent as false. `anonymous_alerts` is
   *   {email: bool, push: bool} (true = that channel still accepts
   *   anonymous-author alerts) and is non-null ONLY when
   *   post_author_is_anonymous is true; null/absent means "no data" and must
   *   render nothing;
   *   real -> {post_id, post_created, matched_users, leads_created,
   *            leads_skipped_existing, notifications_sent, selected_users,
   *            notifications_suppressed_anonymous, suppressed_user_ids}
   *   where selected_users is null unless recipient_user_ids narrowed delivery,
   *   and notifications_suppressed_anonymous (default 0) counts alerts withheld
   *   per the recipients' own preference - withheld is never a failure.
   */
  createManualLead: async (payload) => {
    const response = await api.post("/admin/leads/manual", payload);
    return response.data;
  },

  /**
   * Draft professional-facing Hebrew reasoning + heat + location with Claude.
   * @param {string} content
   * @param {string} professionKey
   * @param {string} [guidance] - Optional admin note steering the model
   *   (not shown to the professional).
   * @returns {Promise<{reasoning, heat_rating, requested_location}>}
   */
  generateLeadReasoning: async (content, professionKey, guidance) => {
    const response = await api.post("/admin/leads/manual/generate-reasoning", {
      content,
      profession_key: professionKey,
      ...(guidance ? { guidance } : {}),
    });
    return response.data;
  },
};

export default adminPostsService;
